// src/components/planner/components/EventDetailPopover.tsx

import React, { useEffect, useRef } from 'react';
import { X, Pencil, Clock, MapPin, Tag, FileText, Calendar } from 'lucide-react';

interface EventDetailPopoverProps {
    event: any;
    anchorEl: HTMLElement;
    onClose: () => void;
    onEdit: () => void;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
    event: 'Evento',
    congress: 'Congresso',
    workshop: 'Workshop',
    exam: 'Prova',
    meeting: 'Reunião',
    lecture: 'Palestra',
    extracurricular: 'Atividade Extracurricular',
    tutoring: 'Monitoria',
    mock_exam: 'Simulado',
    other: 'Outro',
};

const EventDetailPopover: React.FC<EventDetailPopoverProps> = ({
    event,
    anchorEl,
    onClose,
    onEdit,
}) => {
    console.log('🟣 EventDetailPopover RENDERIZOU', event?.title);
    const popoverRef = useRef<HTMLDivElement>(null);

    // Posicionamento dinâmico
    useEffect(() => {
        const popover = popoverRef.current;
        if (!popover) return;

        const rect = anchorEl.getBoundingClientRect();
        const popW = 280;
        const popH = 260; // altura estimada
        const margin = 8;
        const viewportH = window.innerHeight;
        const viewportW = window.innerWidth;

        // Tenta abrir abaixo, se não couber abre acima
        let top = rect.bottom + margin;
        if (top + popH > viewportH - margin) {
            top = rect.top - popH - margin;
        }
        // Se ainda não couber (tela muito pequena), fixa no centro
        if (top < margin) {
            top = viewportH / 2 - popH / 2;
        }

        // Alinha à esquerda do anchor, ajusta se sair da tela
        let left = rect.left;
        if (left + popW > viewportW - margin) {
            left = viewportW - popW - margin;
        }
        if (left < margin) left = margin;

        popover.style.left = `${left}px`;
        popover.style.top = `${top}px`;

        console.log('🟣 Posição final:', { top, left, viewportH, viewportW });
    }, [anchorEl]);

    // ✅ CORRIGIDO — delay de 200ms para não capturar o clique que abriu
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                !anchorEl.contains(e.target as Node)
            ) {
                onClose();
            }
        };

        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 200);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose, anchorEl]);

    // Fechar com Escape
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const typeLabel =
        event.type === 'other' && event.customType
            ? event.customType
            : EVENT_TYPE_LABELS[event.type] || event.type;

    const formattedDate = (() => {
        try {
            const d = new Date(event.date);
            const local = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
            return local.toLocaleDateString('pt-BR', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric',
            });
        } catch {
            return event.date;
        }
    })();

    return (
        <div
            ref={popoverRef}
            style={{
                position: 'fixed',
                width: 280,
                background: 'var(--plnr-bg-card)',
                border: '1px solid var(--plnr-border)',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                zIndex: 99999,
                overflow: 'hidden',
                animation: 'plnr-fadeIn 0.15s ease',
            }}
            onClick={e => e.stopPropagation()}
        >
            {/* Barra de cor no topo */}
            <div style={{ height: 4, background: event.color || 'var(--platform-accent-light)' }} />

            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: '12px 14px 8px',
                gap: 8,
            }}>
                <span style={{
                    fontSize: 14, fontWeight: 700,
                    color: 'var(--plnr-text-primary)',
                    lineHeight: 1.3, flex: 1,
                }}>
                    {event.title}
                </span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--plnr-text-secondary)', padding: 2, flexShrink: 0,
                        display: 'flex', alignItems: 'center',
                    }}
                >
                    <X size={15} />
                </button>
            </div>

            {/* Detalhes */}
            <div style={{
                padding: '0 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
            }}>
                {/* Data */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Calendar size={13} style={{ color: 'var(--plnr-primary)', flexShrink: 0 }} />
                    <span style={{
                        fontSize: 12, color: 'var(--plnr-text-secondary)',
                        textTransform: 'capitalize',
                    }}>
                        {formattedDate}
                    </span>
                </div>

                {/* Horário */}
                {event.time && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Clock size={13} style={{ color: 'var(--plnr-primary)', flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: 'var(--plnr-text-secondary)' }}>
                            {event.time.substring(0, 5)}
                        </span>
                    </div>
                )}

                {/* Tipo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag size={13} style={{ color: 'var(--plnr-primary)', flexShrink: 0 }} />
                    <span style={{
                        fontSize: 11, fontWeight: 600,
                        padding: '2px 8px',
                        background: event.color ? `${event.color}20` : 'var(--platform-a12)',
                        color: event.color || 'var(--platform-accent-light)',
                        borderRadius: 10,
                    }}>
                        {typeLabel}
                    </span>
                </div>

                {/* Local */}
                {event.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MapPin size={13} style={{ color: 'var(--plnr-primary)', flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: 'var(--plnr-text-secondary)' }}>
                            {event.location}
                        </span>
                    </div>
                )}

                {/* Notas */}
                {event.notes && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <FileText size={13} style={{
                            color: 'var(--plnr-primary)',
                            flexShrink: 0, marginTop: 1,
                        }} />
                        <span style={{
                            fontSize: 12, color: 'var(--plnr-text-secondary)',
                            lineHeight: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        } as React.CSSProperties}>
                            {event.notes}
                        </span>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div style={{
                borderTop: '1px solid var(--plnr-border)',
                padding: '10px 14px',
                display: 'flex',
                justifyContent: 'flex-end',
            }}>
                <button
                    onClick={() => { onClose(); onEdit(); }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '7px 14px',
                        background: 'var(--plnr-primary)',
                        color: 'white', border: 'none',
                        borderRadius: 8, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    <Pencil size={13} />
                    Editar evento
                </button>
            </div>
        </div>
    );
};

export default EventDetailPopover;